import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ className = "h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_smartgrid-hub-3/artifacts/huzue581_Nexintell%20logo.png"
    alt="Nexintell" 
    className={`${className} object-contain`}
  />
);

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    // Check if already logged in - redirect to dashboard
    const token = localStorage.getItem('nexintell_token');
    if (token) {
      axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        navigate('/admin/dashboard', { replace: true });
      }).catch(() => {
        localStorage.removeItem('nexintell_token');
        localStorage.removeItem('nexintell_user');
        setCheckingAuth(false);
      });
    } else {
      setCheckingAuth(false);
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      const { access_token, user } = response.data;
      
      // Store token and user data
      localStorage.setItem('nexintell_token', access_token);
      localStorage.setItem('nexintell_user', JSON.stringify(user));
      
      toast.success('Login successful!');
      
      // Use React Router navigation - cleaner and more reliable
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Invalid credentials';
      setError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  // Show loading while checking existing auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#ff4b2a] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <div className="absolute inset-0 hero-glow opacity-50" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-panel rounded-2xl p-8">
          <div className="flex justify-center mb-8">
            <a href="/"><Logo className="h-10" /></a>
          </div>
          
          <h1 className="text-2xl font-semibold text-center mb-2">Admin Dashboard</h1>
          <p className="text-[#a1a1a1] text-center mb-8">Sign in to access CTA analytics</p>
          
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg p-3 mb-6">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-[#a1a1a1] mb-2 block">Email</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="input-field"
              />
            </div>
            
            <div>
              <label className="text-sm text-[#a1a1a1] mb-2 block">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="input-field pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a1a1a1] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" disabled={loading} className="btn-primary w-full mt-6">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="h-5 w-5" />Sign In
                </span>
              )}
            </Button>
          </form>
          
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-[#a1a1a1]">Contact your administrator for login credentials</p>
          </div>
        </div>
        
        <p className="text-center text-sm text-[#a1a1a1] mt-6">
          <a href="/" className="hover:text-white transition-colors">← Back to website</a>
        </p>
      </motion.div>
    </div>
  );
}
